import { Button, Loader, Switch, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { ArrowLeft } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
    ALL_VENDORS,
    createBomMapping,
    deleteBomMapping,
    getBom,
    getBomProductPicks,
    updateBomQuantity,
    type BomMaterialPick,
    type BomRow,
} from '@api/bom-management';
import {
    clearAttributeValue,
    getEntityMasterFilterOptions,
    patchCoreField,
    upsertAttributeValue,
    type MasterTableColumnMeta,
} from '@api/entity-attributes';
import { getWarehouseStock, type WarehouseStockResponse } from '@api/inventory';
import { getMaterialStockSnapshot } from '@api/material-stock-snapshot';
import { createProductVendorMapping, deleteProductVendorMapping, listVendors, vendorInfoOf, type Vendor, type VendorInfo } from '@api/vendors';
import PageHeader from '@components/common/PageHeader';
import AddMaterialModal from '@components/containers/EntityMaster/AddMaterialModal';
import AddVendorModal from '@components/containers/EntityMaster/AddVendorModal';
import AddVendorPriceModal, { type VendorPricePreset } from '@components/containers/EntityMaster/AddVendorPriceModal';
import { bomVendorPills, copySharedBomLines, loadBomMaterialPicks, type BomLineDraft } from '@components/containers/EntityMaster/bomOptions';
import BomRows, { type BomRowMeta } from '@components/containers/EntityMaster/BomRows';
import InlineField from '@components/containers/EntityMaster/InlineField';
import { loadCurrencyOptions, loadMakerVendorOptions, type MakerDraft, type SelectOption } from '@components/containers/EntityMaster/makerOptions';
import MakerRows, { type MakerRowMeta, type SavedMaker } from '@components/containers/EntityMaster/MakerRows';
import { PriceHistoryModal } from '@components/containers/EntityMaster/PriceHistoryModal';
import ProductTypeSelect from '@components/containers/EntityMaster/ProductTypeSelect';
import { BundleKindPill } from '@components/containers/sales/BundleCells';
import { renderDohValue } from '@components/containers/inventory';
import SaveState, { type SaveStatus } from '@components/ui/SaveState';
import StatusPill from '@components/ui/StatusPill';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { semanticColors } from '@styles/design-system';
import { loadRelations } from '@hooks/useSkuExplosion';
import { readApiError } from '@utils/apiError';
import { formatCount } from '@utils/currency';
import { COMMON_GST_RATES } from '@utils/tax';

import { cell, loadMappingRows, loadMasterRow } from './masterRow';
import styles from './record.module.css';

/** Numeric product columns: a number box, and '' clears. */
const NUMERIC_KEYS = new Set(['price', 'weightGrams', 'volumeCbm', 'gstRate', 'unitsPerCase']);
const DECIMALS: Record<string, number> = { price: 2, weightGrams: 2, volumeCbm: 4, gstRate: 2, unitsPerCase: 0 };
/** Columns the page draws itself, or not at all. */
const HANDLED_KEYS = new Set(['sku', 'isActive', 'productType', 'vendors', 'bomLines', 'name', 'category', 'productGroup', 'ean']);
/** Columns offered as a pick list: the brand's own values, or a fixed set, free typing kept. */
type ListedKey = 'subCategory' | 'gstRate';
const PRODUCT_TYPE_TONE: Record<string, 'info' | 'warning' | 'neutral'> = { SIMPLE: 'info', BUNDLE: 'warning', COMBO: 'warning' };
const COMBOS_SHOWN = 8;

/** The combos this product goes into, read off the relation graph. */
interface ComboMembership {
    comboId: string;
    comboName: string;
    quantity: number;
    kind: 'bundle' | 'multipack';
}

/**
 * One product, on a page of its own: details, who makes it, what goes into it, and the
 * read-only context beside them (status, combos it belongs to, stock and demand).
 *
 * Every field writes as it is left, through the same calls the grid and the mapping and
 * BOM tabs make, and reports its own save state. Reads come from the same master-table
 * query the grid runs, narrowed to this id, so a value here is the value there.
 */
const ProductRecord: React.FC = () => {
    const { productId = '' } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const isAdmin = currentUser?.role === 'ADMIN';

    const [row, setRow] = useState<Record<string, unknown> | null>(null);
    const [columns, setColumns] = useState<MasterTableColumnMeta[]>([]);
    const [missing, setMissing] = useState(false);
    const [overrides, setOverrides] = useState<Record<string, string>>({});
    // The grid's own filter options double as the pick lists: category, sub-category, group.
    const [lists, setLists] = useState<Record<string, string[]>>({});
    const [activeStatus, setActiveStatus] = useState<SaveStatus>('idle');
    const [activeError, setActiveError] = useState<string | null>(null);
    const [typeDraft, setTypeDraft] = useState('');
    const [typeStatus, setTypeStatus] = useState<SaveStatus>('idle');
    const [typeError, setTypeError] = useState<string | null>(null);

    const [vendorNames, setVendorNames] = useState<Map<string, VendorInfo>>(new Map());
    const [vendorOptions, setVendorOptions] = useState<SelectOption[]>([]);
    const [currencyOptions, setCurrencyOptions] = useState<string[]>([]);
    const [makers, setMakers] = useState<SavedMaker[]>([]);
    const [makerDrafts, setMakerDrafts] = useState<MakerDraft[]>([]);
    const [savedMeta, setSavedMeta] = useState<Record<string, MakerRowMeta>>({});
    const [draftMeta, setDraftMeta] = useState<Record<number, MakerRowMeta>>({});
    const [historyRow, setHistoryRow] = useState<SavedMaker | null>(null);
    const [pricePreset, setPricePreset] = useState<VendorPricePreset | null>(null);

    const [picks, setPicks] = useState<BomMaterialPick[]>([]);
    const [bomAll, setBomAll] = useState<BomRow[]>([]);
    const [recipe, setRecipe] = useState<string | null>(null);
    const [lineDrafts, setLineDrafts] = useState<BomLineDraft[]>([]);
    const [lineMeta, setLineMeta] = useState<Record<number, BomRowMeta>>({});
    const [copying, setCopying] = useState(false);
    const [stockByMaterial, setStockByMaterial] = useState<Map<string, number>>(new Map());

    const [combos, setCombos] = useState<ComboMembership[]>([]);
    // A Troovy chip sits in forty combos; the card shows a handful until asked for all.
    const [allCombos, setAllCombos] = useState(false);
    const [stock, setStock] = useState<WarehouseStockResponse | null>(null);

    const [newVendorOpen, setNewVendorOpen] = useState(false);
    const [newMaterialOpen, setNewMaterialOpen] = useState(false);

    const valueOf = useCallback(
        (key: string): string | null => (key in overrides ? overrides[key] : cell(row, key)),
        [overrides, row],
    );
    const productName = valueOf('name') || productId;

    // ---- reads ---------------------------------------------------------------------------

    const loadRow = useCallback(async () => {
        const result = await loadMasterRow('PRODUCT', productId).catch(() => null);
        if (!result) {
            setMissing(true);
            return;
        }
        setRow(result.row);
        setColumns(result.columns);
        setOverrides({});
    }, [productId]);

    const loadMakers = useCallback(async () => {
        try {
            const rows = await loadMappingRows('VENDOR_PRODUCT_MAPPING', productId);
            setMakers(
                rows.map((r) => ({
                    rowId: String(r._id ?? ''),
                    vendorId: String(r.vendorId ?? ''),
                    vendorName: '',
                    leadTime: null,
                    price: cell(r, 'price'),
                    currency: cell(r, 'currency'),
                    term: cell(r, 'moq'),
                    priceSince: cell(r, 'priceSince'),
                    priceHistory: cell(r, 'priceHistory'),
                })),
            );
        } catch {
            setMakers([]);
        }
    }, [productId]);

    const loadBom = useCallback(async () => {
        try {
            const bom = await getBom();
            const mine = bom.filter((l) => l.productId === productId);
            setBomAll(mine);
            const materialIds = [...new Set(mine.map((l) => l.materialId))];
            if (materialIds.length === 0) {
                setStockByMaterial(new Map());
                return;
            }
            const snapshot = await getMaterialStockSnapshot({ materialIds }).catch(() => ({ rows: [] }));
            const totals = new Map<string, number>();
            for (const s of snapshot.rows) totals.set(s.materialId, (totals.get(s.materialId) ?? 0) + s.quantity);
            setStockByMaterial(totals);
        } catch {
            setBomAll([]);
        }
    }, [productId]);

    const loadContext = useCallback(async () => {
        // The whole network: own warehouses plus channel stock, the Inventory page's Overall
        // scope. Channel rollups come back without a warehouse_name, which is how the
        // Inventory page tells them apart too.
        void getWarehouseStock({ productIds: [productId], type: 'OVERALL', groupBy: 'warehouse', pageSize: 1 })
            .then(setStock)
            .catch(() => setStock(null));
        void Promise.all([loadRelations(), getBomProductPicks().catch(() => [])])
            .then(([edges, products]) => {
                const names = new Map(products.map((p) => [p.productId, p.name]));
                const parents = edges.filter((e) => e.childId === productId);
                const sizes = new Map<string, number>();
                for (const e of edges) sizes.set(e.parentId, (sizes.get(e.parentId) ?? 0) + 1);
                setCombos(
                    parents.map((e) => ({
                        comboId: e.parentId,
                        comboName: names.get(e.parentId) ?? e.parentId,
                        quantity: Number(e.quantity) || 1,
                        kind: (sizes.get(e.parentId) ?? 1) > 1 ? 'bundle' : 'multipack',
                    })),
                );
            })
            .catch(() => setCombos([]));
    }, [productId]);

    useEffect(() => {
        setMissing(false);
        setRow(null);
        setMakerDrafts([]);
        setDraftMeta({});
        setSavedMeta({});
        setLineDrafts([]);
        setLineMeta({});
        setRecipe(null);
        setActiveStatus('idle');
        setTypeStatus('idle');
        void loadRow();
        void loadMakers();
        void loadBom();
        void loadContext();
    }, [loadRow, loadMakers, loadBom, loadContext]);

    useEffect(() => {
        setTypeDraft(cell(row, 'productType') ?? '');
    }, [row]);

    useEffect(() => {
        void loadMakerVendorOptions().then(setVendorOptions);
        void loadCurrencyOptions().then(setCurrencyOptions);
        void loadBomMaterialPicks().then(setPicks);
        void listVendors()
            .then((vendors) => setVendorNames(new Map(vendors.map((v) => [v.vendorId, vendorInfoOf(v)]))))
            .catch(() => undefined);
        void getEntityMasterFilterOptions('PRODUCT').then(setLists).catch(() => undefined);
    }, []);

    const namedMakers = useMemo(
        () => makers.map((m) => ({ ...m, vendorName: vendorNames.get(m.vendorId)?.name ?? m.vendorName, leadTime: vendorNames.get(m.vendorId)?.leadTime ?? null })),
        [makers, vendorNames],
    );

    // ---- details -------------------------------------------------------------------------

    const saveCore = useCallback(
        async (key: string, value: string) => {
            await patchCoreField('PRODUCT', productId, key, value);
            setOverrides((prev) => ({ ...prev, [key]: value }));
        },
        [productId],
    );

    const isActive = (valueOf('isActive') ?? 'true') !== 'false';

    const saveActive = async (checked: boolean) => {
        setActiveStatus('saving');
        setActiveError(null);
        try {
            await saveCore('isActive', checked ? 'true' : 'false');
            setActiveStatus('saved');
        } catch (err) {
            setActiveError(readApiError(err, 'Could not save'));
            setActiveStatus('failed');
        }
    };

    const confirmActive = (checked: boolean) => {
        if (checked) {
            void saveActive(true);
            return;
        }
        modals.openConfirmModal({
            title: `Deactivate ${productName}?`,
            children: (
                <Text size="sm">
                    An inactive product drops out of the pickers for orders, transfers and recipes. Its stock,
                    history and makers stay as they are, and it can be reactivated here.
                </Text>
            ),
            labels: { confirm: 'Deactivate', cancel: 'Keep active' },
            confirmProps: { color: 'red' },
            onConfirm: () => void saveActive(false),
        });
    };

    const commitType = async () => {
        const next = typeDraft.trim();
        if (next === (valueOf('productType') ?? '')) return;
        setTypeStatus('saving');
        setTypeError(null);
        try {
            await saveCore('productType', next);
            setTypeStatus('saved');
        } catch (err) {
            setTypeError(readApiError(err, 'Could not save'));
            setTypeStatus('failed');
        }
    };

    // ---- makers --------------------------------------------------------------------------

    const setSaved = (rowId: string, meta: MakerRowMeta) =>
        setSavedMeta((prev) => ({ ...prev, [rowId]: { ...prev[rowId], ...meta } }));
    const setDraft = (index: number, meta: MakerRowMeta) =>
        setDraftMeta((prev) => ({ ...prev, [index]: { ...prev[index], ...meta } }));

    const onSavedChange = async (maker: SavedMaker, patch: { currency?: string | null; term?: string }) => {
        setSaved(maker.rowId, { status: 'saving', error: null });
        try {
            if (patch.currency !== undefined) await patchCoreField('VENDOR_PRODUCT_MAPPING', maker.rowId, 'currency', patch.currency ?? '');
            if (patch.term !== undefined) await patchCoreField('VENDOR_PRODUCT_MAPPING', maker.rowId, 'moq', patch.term);
            setSaved(maker.rowId, { status: 'saved' });
            await loadMakers();
        } catch (err) {
            setSaved(maker.rowId, { status: 'failed', error: readApiError(err, 'Could not save') });
        }
    };

    const commitDraft = async (index: number) => {
        const draft = makerDrafts[index];
        if (!draft?.vendorId) return;
        if (makers.some((m) => m.vendorId === draft.vendorId)) {
            setDraft(index, { status: 'failed', error: 'Already a maker of this product' });
            return;
        }
        setDraft(index, { status: 'saving', error: null });
        try {
            await createProductVendorMapping({
                productId,
                vendorId: draft.vendorId,
                price: draft.price === '' || !isAdmin ? undefined : Number(draft.price),
                currency: draft.currency ?? undefined,
            });
            setMakerDrafts((prev) => prev.filter((_, i) => i !== index));
            setDraftMeta({});
            await loadMakers();
            void loadRow();
        } catch (err) {
            setDraft(index, { status: 'failed', error: readApiError(err, 'Could not add the maker') });
        }
    };

    const removeMaker = (maker: SavedMaker) => {
        modals.openConfirmModal({
            title: `Remove ${maker.vendorName || maker.vendorId} as a maker?`,
            children: (
                <Text size="sm">
                    {productName} will no longer be ordered from this maker. Its current price line ends today and
                    stays in the history; orders already raised are not touched. Any recipe of its own is kept.
                </Text>
            ),
            labels: { confirm: 'Remove', cancel: 'Keep' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setSaved(maker.rowId, { status: 'saving', error: null });
                try {
                    await deleteProductVendorMapping(productId, maker.vendorId);
                    await loadMakers();
                    void loadRow();
                } catch (err) {
                    setSaved(maker.rowId, { status: 'failed', error: readApiError(err, 'Could not remove the maker') });
                }
            },
        });
    };

    const onVendorCreated = (vendor: Vendor) => {
        setVendorOptions((prev) => [...prev, { value: vendor.vendorId, label: `${vendor.name} — ${vendor.vendorId}` }]);
        setVendorNames((prev) => new Map(prev).set(vendor.vendorId, vendorInfoOf(vendor)));
        setMakerDrafts((prev) => [...prev, { vendorId: vendor.vendorId, price: '', currency: null }]);
        setNewVendorOpen(false);
    };

    // ---- BOM lines -----------------------------------------------------------------------

    const recipeLines = useMemo(
        () => bomAll.filter((l) => (recipe ? l.vendorId === recipe : l.vendorId === null)),
        [bomAll, recipe],
    );
    const sharedLines = useMemo(() => bomAll.filter((l) => l.vendorId === null), [bomAll]);
    // Lines per recipe for the chips: the shared pill keys the brand recipe (''); the
    // "all lines" pill is the grid's, not a recipe, and is skipped.
    const recipeCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const pill of bomVendorPills(bomAll)) {
            if (pill.value === '') continue;
            counts[pill.value === ALL_VENDORS ? '' : pill.value] = pill.count;
        }
        return counts;
    }, [bomAll]);
    const recipeOptions: SelectOption[] = useMemo(() => {
        const seen = new Map<string, string>();
        for (const m of namedMakers) seen.set(m.vendorId, m.vendorName || m.vendorId);
        for (const l of bomAll) if (l.vendorId && !seen.has(l.vendorId)) seen.set(l.vendorId, l.vendorName ?? l.vendorId);
        return [...seen.entries()].map(([value, label]) => ({ value, label }));
    }, [namedMakers, bomAll]);

    const lineRows: BomLineDraft[] = useMemo(
        () => [...recipeLines.map((l) => ({ materialId: l.materialId, quantity: l.quantity })), ...lineDrafts],
        [recipeLines, lineDrafts],
    );
    const linePicks = useMemo(() => {
        // A line may reference a material the picker no longer offers (retired since); keep it
        // visible so the row still shows its name and unit.
        const known = new Set(picks.map((p) => p.materialId));
        const extra: BomMaterialPick[] = recipeLines
            .filter((l) => !known.has(l.materialId))
            .map((l) => ({ materialId: l.materialId, sku: l.materialSku, name: l.materialName, materialType: l.materialType, unit: l.unit }));
        return [...picks, ...extra];
    }, [picks, recipeLines]);

    const setLineState = (index: number, meta: BomRowMeta) =>
        setLineMeta((prev) => ({ ...prev, [index]: { ...prev[index], ...meta } }));

    const changeLine = (index: number, patch: Partial<BomLineDraft>) => {
        if (index < recipeLines.length) {
            if (patch.quantity === undefined) return;
            const line = recipeLines[index];
            setBomAll((prev) => prev.map((l) => (l === line ? { ...l, quantity: patch.quantity === '' ? 0 : Number(patch.quantity) } : l)));
        } else {
            const di = index - recipeLines.length;
            setLineDrafts((prev) => prev.map((d, i) => (i === di ? { ...d, ...patch } : d)));
        }
    };

    const commitLine = async (index: number) => {
        if (index < recipeLines.length) {
            const line = recipeLines[index];
            if (!(line.quantity > 0)) {
                setLineState(index, { status: 'failed', error: 'Quantity must be above zero. Remove the line instead.' });
                return;
            }
            setLineState(index, { status: 'saving', error: null, note: null });
            try {
                await updateBomQuantity(line.productId, line.materialId, line.quantity, line.vendorId);
                setLineState(index, { status: 'saved' });
            } catch (err) {
                setLineState(index, { status: 'failed', error: readApiError(err, 'Could not save') });
            }
            return;
        }
        const di = index - recipeLines.length;
        const draft = lineDrafts[di];
        if (!draft?.materialId || draft.quantity === '' || !(Number(draft.quantity) > 0)) return;
        setLineState(index, { status: 'saving', error: null, note: null });
        try {
            const result = await createBomMapping({ productId, materialId: draft.materialId, quantity: Number(draft.quantity), vendorId: recipe });
            setLineDrafts((prev) => prev.filter((_, i) => i !== di));
            setLineMeta({});
            await loadBom();
            if (result?.outcome === 'updated') setLineState(recipeLines.length, { note: 'Updated the existing line for this material' });
            void loadRow();
        } catch (err) {
            setLineState(index, { status: 'failed', error: readApiError(err, 'Could not add the line') });
        }
    };

    const removeLine = async (index: number) => {
        if (index < recipeLines.length) {
            const line = recipeLines[index];
            setLineState(index, { status: 'saving', error: null });
            try {
                await deleteBomMapping(line.productId, line.materialId, line.vendorId);
                setLineMeta({});
                await loadBom();
                void loadRow();
            } catch (err) {
                setLineState(index, { status: 'failed', error: readApiError(err, 'Could not remove the line') });
            }
            return;
        }
        const di = index - recipeLines.length;
        setLineDrafts((prev) => prev.filter((_, i) => i !== di));
        setLineMeta({});
    };

    const copyBrandRecipe = async () => {
        if (!recipe) return;
        setCopying(true);
        try {
            await copySharedBomLines(bomAll, productId, recipe);
            await loadBom();
        } finally {
            setCopying(false);
        }
    };

    const onMaterialCreated = (material: { materialId: string; name: string; materialType: string; unit: string; sku?: string | null }) => {
        setPicks((prev) => [
            ...prev,
            { materialId: material.materialId, sku: material.sku ?? null, name: material.name, materialType: material.materialType as BomMaterialPick['materialType'], unit: material.unit },
        ]);
        setLineDrafts((prev) => [...prev, { materialId: material.materialId, quantity: '' }]);
        setNewMaterialOpen(false);
    };

    // ---- render --------------------------------------------------------------------------

    if (missing) {
        return (
            <div className="flex h-full w-full flex-col gap-4 page-shell">
                <PageHeader title="Product not found" subtitle={productId} actions={<BackToCatalog view="products" />} />
            </div>
        );
    }
    if (!row) {
        return (
            <div className="flex h-full w-full items-center justify-center page-shell">
                <Loader size="sm" />
            </div>
        );
    }

    const detailColumns = columns.filter((c) => c.editable && !HANDLED_KEYS.has(c.key));
    const listed: Record<ListedKey, string[]> = { subCategory: lists.subCategory ?? [], gstRate: COMMON_GST_RATES };
    const productType = (valueOf('productType') ?? '').trim();
    const stockItem = stock?.data?.find((d) => d.product_id === productId);
    const stockLines = Object.entries(stockItem?.groupings ?? {}).filter(([, g]) => g.quantity > 0);
    const ownStockLines = stockLines.filter(([, g]) => !!g.warehouse_name);
    const channelStockLines = stockLines.filter(([, g]) => !g.warehouse_name);
    const limits = stock?.inventory_config;

    return (
        <div className="flex h-full w-full flex-col gap-4 page-shell">
            <PageHeader
                title={productName}
                subtitle={[valueOf('sku'), valueOf('category')].filter(Boolean).join(' · ')}
                actions={<BackToCatalog view="products" />}
            />
            <div className={styles.layout}>
                <div className={styles.main}>
                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>1</span>
                            <span className={styles.sectionTitle}>Details</span>
                            <span className={styles.sectionSub}>fields save as you leave them</span>
                        </div>
                        <div className={styles.grid}>
                            <div className={styles.span2}>
                                <InlineField label="Name" value={valueOf('name')} onSave={(v) => saveCore('name', v)} />
                            </div>
                            <InlineField
                                label="SKU"
                                value={valueOf('sku')}
                                onSave={async () => undefined}
                                readOnlyReason="The SKU is what the WMS and channel feeds match on; it is changed by a catalogue cleanup, not here"
                                mono
                            />
                            <InlineField label="EAN / barcode" value={valueOf('ean')} onSave={(v) => saveCore('ean', v)} hint="GTIN, 8 to 14 digits" mono />
                            <InlineField label="Category" value={valueOf('category')} kind="autocomplete" options={lists.category ?? []} onSave={(v) => saveCore('category', v)} />
                            <InlineField label="Group" value={valueOf('productGroup')} kind="autocomplete" options={lists.productGroup ?? []} onSave={(v) => saveCore('productGroup', v)} />
                            <div className={styles.field}>
                                <div className={styles.fieldLabelRow}>
                                    <span className={styles.fieldLabel}>Type</span>
                                    <SaveState status={typeStatus} message={typeError} />
                                </div>
                                <ProductTypeSelect
                                    label=""
                                    size="xs"
                                    value={typeDraft}
                                    onChange={setTypeDraft}
                                    onBlur={() => void commitType()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') void commitType();
                                    }}
                                />
                            </div>
                            {detailColumns.map((col) => (
                                <InlineField
                                    key={col.key}
                                    label={col.header}
                                    value={valueOf(col.key)}
                                    kind={listed[col.key as ListedKey] ? 'autocomplete' : NUMERIC_KEYS.has(col.key) ? 'number' : 'text'}
                                    options={listed[col.key as ListedKey]}
                                    decimalScale={DECIMALS[col.key]}
                                    onSave={async (v) => {
                                        if (col.type === 'ATTRIBUTE' && col.attributeId) {
                                            if (v === '') await clearAttributeValue(col.attributeId, productId);
                                            else await upsertAttributeValue(col.attributeId, productId, v);
                                            setOverrides((prev) => ({ ...prev, [col.key]: v }));
                                            return;
                                        }
                                        await saveCore(col.key, v);
                                    }}
                                />
                            ))}
                        </div>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>2</span>
                            <span className={styles.sectionTitle}>Makers and prices</span>
                            <span className={styles.sectionCount}>
                                <StatusPill label={`${makers.length} ${makers.length === 1 ? 'maker' : 'makers'}`} tone={makers.length ? 'positive' : 'warning'} />
                            </span>
                        </div>
                        <MakerRows
                            makers={namedMakers}
                            drafts={makerDrafts}
                            vendorOptions={vendorOptions}
                            currencyOptions={currencyOptions}
                            isAdmin={isAdmin}
                            onSavedChange={(m, patch) => void onSavedChange(m, patch)}
                            onSetPrice={(m) => setPricePreset({ vendorId: m.vendorId, itemType: 'product', itemId: productId, itemLabel: productName })}
                            onShowHistory={setHistoryRow}
                            onRemove={removeMaker}
                            savedMeta={(rowId) => savedMeta[rowId]}
                            onDraftChange={(i, patch) => setMakerDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)))}
                            onDraftCommit={(i) => void commitDraft(i)}
                            onDraftRemove={(i) => {
                                setMakerDrafts((prev) => prev.filter((_, j) => j !== i));
                                setDraftMeta({});
                            }}
                            draftMeta={(i) => draftMeta[i]}
                            onAdd={() => setMakerDrafts((prev) => [...prev, { vendorId: null, price: '', currency: null }])}
                            onNewVendor={isAdmin ? () => setNewVendorOpen(true) : undefined}
                        />
                        <span className={styles.footer}>A price change starts a new dated price; the old one stays in the history.</span>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.sectionHead}>
                            <span className={styles.sectionNumber}>3</span>
                            <span className={styles.sectionTitle}>Bill of materials</span>
                            <span className={styles.sectionSub}>per one unit</span>
                            <span className={styles.sectionCount}>
                                <StatusPill label={`${recipeLines.length} ${recipeLines.length === 1 ? 'line' : 'lines'}`} tone={recipeLines.length ? 'positive' : 'warning'} />
                            </span>
                        </div>
                        <BomRows
                            rows={lineRows}
                            picks={linePicks}
                            onChange={changeLine}
                            onCommit={(i) => void commitLine(i)}
                            onAdd={() => setLineDrafts((prev) => [...prev, { materialId: null, quantity: '' }])}
                            onRemove={(i) => void removeLine(i)}
                            onNewMaterial={isAdmin ? () => setNewMaterialOpen(true) : undefined}
                            materialLocked={(i) => i < recipeLines.length}
                            meta={(i) => lineMeta[i]}
                            recipe={{
                                value: recipe,
                                options: recipeOptions,
                                counts: recipeCounts,
                                onChange: (v) => {
                                    setRecipe(v);
                                    setLineDrafts([]);
                                    setLineMeta({});
                                },
                            }}
                            stockByMaterial={stockByMaterial}
                            copyShared={recipe && recipeLines.length === 0 ? { onCopy: () => void copyBrandRecipe(), busy: copying, sharedCount: sharedLines.length } : undefined}
                        />
                    </section>
                </div>

                <aside className={styles.rail}>
                    <div className={styles.card}>
                        <span className={styles.cardTitle}>Status</span>
                        <div className={styles.factRow}>
                            <Switch size="sm" color={semanticColors.info.primary} label="Active" checked={isActive} onChange={(e) => confirmActive(e.currentTarget.checked)} disabled={activeStatus === 'saving'} />
                            <SaveState status={activeStatus} message={activeError} />
                        </div>
                        <div className={styles.factRow}>
                            <span>Type</span>
                            {productType ? <StatusPill label={productType.toUpperCase()} tone={PRODUCT_TYPE_TONE[productType.toUpperCase()] ?? 'neutral'} /> : <span className={styles.muted}>not set</span>}
                        </div>
                        <div className={styles.factRow}>
                            <span>Makers</span>
                            <span>{makers.length}</span>
                        </div>
                        <div className={styles.factRow}>
                            <span>Recipe lines</span>
                            <span>{sharedLines.length}{bomAll.length > sharedLines.length ? ` (+${bomAll.length - sharedLines.length} maker-specific)` : ''}</span>
                        </div>
                    </div>

                    <div className={styles.card}>
                        <span className={styles.cardTitle}>Part of combos</span>
                        {combos.length === 0 ? (
                            <span className={styles.muted}>Not part of any combo.</span>
                        ) : (
                            <>
                                {(allCombos ? combos : combos.slice(0, COMBOS_SHOWN)).map((c) => (
                                    <div key={c.comboId} className={styles.factRow}>
                                        <button type="button" className={styles.linkButton} onClick={() => void navigate(`/catalog/products/${encodeURIComponent(c.comboId)}`)}>
                                            {c.comboName}
                                        </button>
                                        <span className={styles.muted}>
                                            ×{c.quantity} <BundleKindPill kind={c.kind} />
                                        </span>
                                    </div>
                                ))}
                                {combos.length > COMBOS_SHOWN && (
                                    <button type="button" className={styles.linkButton} onClick={() => setAllCombos((v) => !v)}>
                                        {allCombos ? 'Show fewer' : `Show all ${combos.length}`}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    <div className={styles.card}>
                        <span className={styles.cardTitle}>Stock and demand</span>
                        {!stockItem ? (
                            <span className={styles.muted}>No stock on file.</span>
                        ) : (
                            <>
                                <div className={styles.factRow}>
                                    <span>On hand</span>
                                    <span>{formatCount(stockItem.quantity)}</span>
                                </div>
                                {stockItem.in_transit != null && stockItem.in_transit > 0 && (
                                    <div className={styles.factRow}>
                                        <span>In transit</span>
                                        <span>{formatCount(stockItem.in_transit)}</span>
                                    </div>
                                )}
                                <div className={styles.factRow}>
                                    <span>Demand per day</span>
                                    <span>{stockItem.consumption_per_day != null ? stockItem.consumption_per_day.toFixed(1) : 'NA'}</span>
                                </div>
                                <div className={styles.factRow}>
                                    <span>Days on hand</span>
                                    <span>{renderDohValue(stockItem.days_on_hand, limits?.under_stock_limit ?? 0, limits?.over_stock_limit ?? Infinity)}</span>
                                </div>
                                {ownStockLines.length > 0 && <span className={styles.stockGroup}>Own warehouses</span>}
                                {ownStockLines.map(([key, g]) => (
                                    <div key={key} className={styles.factRow}>
                                        <span className={styles.muted}>{g.warehouse_name}</span>
                                        <span>{formatCount(g.quantity)}</span>
                                    </div>
                                ))}
                                {channelStockLines.length > 0 && <span className={styles.stockGroup}>Channels</span>}
                                {channelStockLines.map(([channel, g]) => (
                                    <div key={channel} className={styles.factRow}>
                                        <span className={styles.muted}>{channel}</span>
                                        <span>{formatCount(g.quantity)}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </aside>
            </div>

            <AddVendorModal opened={newVendorOpen} onClose={() => setNewVendorOpen(false)} onCreated={onVendorCreated} />
            <AddMaterialModal opened={newMaterialOpen} onClose={() => setNewMaterialOpen(false)} onCreated={onMaterialCreated} />
            {pricePreset && (
                <AddVendorPriceModal
                    opened
                    onClose={() => setPricePreset(null)}
                    onCreated={() => void loadMakers()}
                    preset={pricePreset}
                />
            )}
            <PriceHistoryModal
                opened={!!historyRow}
                onClose={() => setHistoryRow(null)}
                title={historyRow ? `${productName} · ${historyRow.vendorName || historyRow.vendorId}` : ''}
                currentPrice={historyRow?.price ?? null}
                currency={historyRow?.currency ?? null}
                priceSince={historyRow?.priceSince ?? null}
                historyText={historyRow?.priceHistory ?? null}
            />
        </div>
    );
};

/** The way back to the list. Browser Back keeps the filters; this lands on the tab fresh. */
export const BackToCatalog: React.FC<{ view: 'products' | 'materials' }> = ({ view }) => {
    const navigate = useNavigate();
    return (
        <Button variant="default" leftSection={<ArrowLeft size={16} />} onClick={() => void navigate(`/catalog?view=${view}`)}>
            All {view}
        </Button>
    );
};

export default ProductRecord;
